import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Upload as UploadIcon, FileText, GitMerge, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle2, AlertTriangle, Loader2, Truck, FileCheck, X,
  ExternalLink, Flag, Search, Link2, History, Download, MessageSquareWarning,
  Activity, RefreshCw,
} from 'lucide-react'
import {
  uploadFedexPdfs, getFedexInvoices, getFedexInvoiceLines,
  getFedexReconciliations, updateFedexReconciliationResolution,
  assignFedexReconciliation, getFedexCourierCostOverrides, getAdminOrders,
  exportFedexDisputes, getFedexWorkerStatus, reprocessFedexInvoice,
  reprocessAllRejectedFedex, getFedexIncompleteReconciliations,
  reconcileFedexInvoice, reconcileAllIncompleteFedex,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'
import WorkerStatusCard from '../../components/admin/WorkerStatusCard'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(v, currency = 'AUD') {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency, minimumFractionDigits: 2,
  }).format(v ?? 0)
}

function formatDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function statusBadge(status) {
  const map = {
    parsed:    { bg: 'bg-emerald-50',  fg: 'text-emerald-700', label: 'Parsed'   },
    rejected:  { bg: 'bg-red-50',      fg: 'text-red-700',     label: 'Rejected' },
    failed:    { bg: 'bg-orange-50',   fg: 'text-orange-700',  label: 'Failed'   },
    pending:   { bg: 'bg-slate-100',   fg: 'text-slate-600',   label: 'Pending'  },
  }
  const s = map[status] || map.pending
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  )
}

function attemptBadge(attempts) {
  // Hidden on the first attempt, which is almost every row — the number only
  // says something once a stub has been retried. A count that keeps climbing
  // is the queue's signal that the classifier is missing a rule for one of the
  // charge labels, rather than a PDF that has only just arrived.
  const n = Number(attempts) || 0
  if (n <= 1) return null
  const stuck = n >= 3
  const retries = `${n - 1} time${n === 2 ? '' : 's'}`
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium tabular-nums ${
        stuck
          ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
          : 'bg-slate-100 text-slate-600'
      }`}
      title={`Attempt ${n} — reprocessed ${retries} since this invoice first arrived.${
        stuck ? ' Repeated reprocessing has not helped; the classifier is probably missing a rule.' : ''
      }`}
    >
      <RefreshCw size={10} />
      {n}
    </span>
  )
}

function resolutionBadge(resolution, reviewedBy = null) {
  // accepted + no reviewer = the ingest pipeline auto-accepted it on a zero
  // variance match. Visually muted so it falls behind the manual-accept badge.
  const isAuto = resolution === 'accepted' && !reviewedBy
  const map = {
    pending:      { bg: 'bg-slate-100',   fg: 'text-slate-700',   label: 'Pending'        },
    accepted:     { bg: 'bg-emerald-50',  fg: 'text-emerald-700', label: 'Accepted'       },
    disputed:     { bg: 'bg-amber-50',    fg: 'text-amber-700',   label: 'Disputed'       },
    written_off:  { bg: 'bg-slate-100',   fg: 'text-slate-500',   label: 'Written off'    },
    dismissed:    { bg: 'bg-slate-100',   fg: 'text-slate-400',   label: 'Dismissed'      },
  }
  const s = isAuto
    ? { bg: 'bg-emerald-50/60', fg: 'text-emerald-600', label: 'Auto-accepted' }
    : (map[resolution] || map.pending)
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.fg}`}
      title={isAuto ? '0% variance — accepted automatically by the ingest pipeline.' : undefined}
    >
      {s.label}
    </span>
  )
}

// A failed useQuery leaves `data` on its `= []` default, so every list on this
// page would otherwise render its reassuring empty state ("No exceptions",
// "Nothing flagged") and $0.00 stat tiles for a fetch that never succeeded.
// This stands in for the empty state so a failure never reads as "all clear".
function QueryErrorState({ error, what, dense = false }) {
  const detail = error?.response?.data?.detail || error?.message || 'Unknown error'
  return (
    <div className={`flex flex-col items-center justify-center gap-2 px-4 ${dense ? 'py-8' : 'py-16'}`}>
      <AlertTriangle size={dense ? 20 : 32} className="text-red-400" />
      <p className="text-sm font-medium text-red-700">Couldn't load {what}</p>
      <p className="text-xs text-red-600 text-center max-w-md">
        This is a load failure, not an empty result — {what} may exist but aren't shown,
        and the figures above are not real totals.
      </p>
      <p className="text-xs text-slate-400 font-mono text-center max-w-md break-all">{detail}</p>
    </div>
  )
}

// ─── Upload Tab ───────────────────────────────────────────────────────────────

function UploadTab() {
  const [files, setFiles] = useState([])
  const [dragOver, setDragOver] = useState(false)
  const [lastResult, setLastResult] = useState(null)
  const { toasts, addToast } = useToast()
  const queryClient = useQueryClient()

  const uploadMutation = useMutation({
    mutationFn: () => uploadFedexPdfs(files),
    onSuccess: (data) => {
      setLastResult(data)
      setFiles([])
      queryClient.invalidateQueries({ queryKey: ['fedex', 'invoices'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      const { parsed, duplicates, rejected, errored } = data.summary || {}
      addToast({
        type: rejected || errored ? 'error' : 'success',
        message: `${parsed} parsed · ${duplicates} duplicates · ${rejected} rejected · ${errored} errored`,
        duration: 6000,
      })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Upload failed',
    }),
  })

  function onDrop(e) {
    e.preventDefault()
    setDragOver(false)
    const dropped = Array.from(e.dataTransfer.files || []).filter(f => f.name.toLowerCase().endsWith('.pdf'))
    if (dropped.length) setFiles(prev => [...prev, ...dropped])
  }

  function onSelect(e) {
    const picked = Array.from(e.target.files || []).filter(f => f.name.toLowerCase().endsWith('.pdf'))
    if (picked.length) setFiles(prev => [...prev, ...picked])
    e.target.value = ''
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} />

      <div className="card border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 space-y-1">
        <p className="font-medium">Manual upload</p>
        <p className="text-blue-600 text-xs">
          Drop FedEx PDF invoices here to ingest them immediately. Same pipeline as the Gmail poller,
          so duplicates (by invoice number or PDF hash) are automatically skipped.
        </p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`card flex flex-col items-center justify-center py-12 border-2 border-dashed transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <UploadIcon size={32} className="text-slate-400 mb-3" />
        <p className="text-sm font-medium text-slate-700">Drop PDFs here</p>
        <p className="text-xs text-slate-500 mt-1">or</p>
        <label className="mt-3 btn-secondary text-sm cursor-pointer">
          Browse files
          <input type="file" accept=".pdf" multiple onChange={onSelect} className="hidden" />
        </label>
        <p className="text-xs text-slate-400 mt-3">Up to 25 MiB per file</p>
      </div>

      {files.length > 0 && (
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <p className="text-sm font-medium text-slate-700">
              {files.length} file{files.length !== 1 ? 's' : ''} ready to upload
            </p>
            <button
              onClick={() => uploadMutation.mutate()}
              disabled={uploadMutation.isPending}
              className="btn-primary text-sm"
            >
              {uploadMutation.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                : <><FileCheck size={14} /> Ingest</>}
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {files.map((f, i) => (
              <li key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                <span className="text-slate-700 font-mono">{f.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">
                    {(f.size / 1024).toFixed(1)} KB
                  </span>
                  <button onClick={() => removeFile(i)} className="text-slate-300 hover:text-red-500">
                    <X size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lastResult && lastResult.results && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <p className="text-sm font-medium text-slate-700">Last upload result</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">File</th>
                <th className="px-4 py-2 text-left">Invoice</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">AWBs</th>
                <th className="px-4 py-2 text-right">Matched</th>
                <th className="px-4 py-2 text-right">Overrides</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lastResult.results.map((r, i) => (
                <tr key={i}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">{r.filename}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.invoice_number || '—'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.invoice_type || '—'}</td>
                  <td className="px-4 py-2">{statusBadge(r.status)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.awbs_processed}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.awbs_matched}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.cost_overrides_applied}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Invoices Tab ─────────────────────────────────────────────────────────────

function InvoicesTab() {
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [expanded, setExpanded] = useState(null)   // invoice id

  const params = {
    ...(typeFilter   && { invoice_type: typeFilter }),
    ...(statusFilter && { status: statusFilter }),
  }

  const { data: invoices = [], isLoading, error: invoicesError } = useQuery({
    queryKey: ['fedex', 'invoices', params],
    queryFn: () => getFedexInvoices(params),
    refetchInterval: 60_000,
  })

  const totalValue = invoices.reduce((s, r) => s + (r.statement_total ?? 0), 0)
  const totalLines = invoices.reduce((s, r) => s + (r.line_count ?? 0), 0)
  const rejectedCount = invoices.filter(r => r.parse_status === 'rejected').length

  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const reprocessAll = useMutation({
    mutationFn: reprocessAllRejectedFedex,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fedex', 'invoices'] })
      queryClient.invalidateQueries({ queryKey: ['sync', 'worker-health'] })
      const { reprocessed = 0, still_failing = 0, deleted_no_pdf = 0, total = 0 } = data || {}
      addToast({
        type: still_failing ? 'error' : 'success',
        message: `Reprocessed ${reprocessed}/${total} · ${still_failing} still failing${deleted_no_pdf ? ` · ${deleted_no_pdf} deleted` : ''}`,
      })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Bulk reprocess failed.' }),
  })

  // Invoices that are stored but never finished reconciling. Nothing in the
  // invoice list can show this on its own: parse_status reads 'parsed' (it IS
  // parsed — the header and lines are committed), so the row looks healthy
  // while the per-AWB reconciliation rows, cost overrides or export_fees rows
  // are missing. It is derived server-side per AWB.
  //
  // Deliberately NOT on the 60s refetch the invoice list uses: the report is
  // four full-table reads (invoices, lines, reconciliations, export fees) and
  // polling it would re-run all of them every minute for every admin with
  // this page open. Fetched on mount and invalidated after a re-run, which is
  // the only thing that changes the answer.
  const { data: incomplete } = useQuery({
    queryKey: ['fedex', 'incomplete-reconciliation'],
    queryFn: getFedexIncompleteReconciliations,
  })
  const incompleteInvoices = incomplete?.invoices ?? []
  const incompleteAwbs = incompleteInvoices.reduce((s, r) => s + (r.awbs_missing ?? 0), 0)

  const reconcileAll = useMutation({
    mutationFn: reconcileAllIncompleteFedex,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fedex', 'incomplete-reconciliation'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'overrides'] })
      const { reconciled = 0, still_failing = 0, total = 0, awbs_reconciled = 0 } = data || {}
      addToast({
        type: still_failing ? 'error' : 'success',
        message: `Reconciled ${reconciled}/${total} invoice${total === 1 ? '' : 's'} · ${awbs_reconciled} AWB${awbs_reconciled === 1 ? '' : 's'}${still_failing ? ` · ${still_failing} still failing` : ''}`,
        duration: 8000,
      })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Bulk reconcile failed.',
    }),
  })

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {incompleteInvoices.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900">
                {incompleteInvoices.length} invoice{incompleteInvoices.length === 1 ? '' : 's'} stored
                with reconciliation unfinished — {incompleteAwbs} AWB
                {incompleteAwbs === 1 ? '' : 's'} not reconciled
              </p>
              <p className="text-xs text-amber-800 mt-1">
                These parsed cleanly and what FedEx billed is recorded, but the reconciliation
                stage didn&apos;t finish, so their cost overrides or export fees are missing from
                billing. Re-running is safe and repeatable — AWBs already done are left alone.
              </p>
              <p className="text-xs text-amber-700 mt-1.5 font-mono">
                {incompleteInvoices.slice(0, 6).map(r => r.invoice_number).join(' · ')}
                {incompleteInvoices.length > 6 && ` · +${incompleteInvoices.length - 6} more`}
              </p>
            </div>
            <button
              onClick={() => reconcileAll.mutate()}
              disabled={reconcileAll.isPending}
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50"
              title="Re-run the reconciliation stage for every invoice listed. Does not re-insert headers or lines."
            >
              {reconcileAll.isPending
                ? <Loader2 size={12} className="animate-spin" />
                : <RefreshCw size={12} />}
              Finish reconciliation
            </button>
          </div>
        </div>
      )}
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="input text-sm py-1 w-auto"
        >
          <option value="">All types</option>
          <option value="shipping">Shipping</option>
          <option value="duty">Duty</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input text-sm py-1 w-auto"
        >
          <option value="">All statuses</option>
          <option value="parsed">Parsed</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>
        {rejectedCount > 0 && (
          <button
            onClick={() => reprocessAll.mutate()}
            disabled={reprocessAll.isPending}
            className="btn-secondary text-sm py-1 ml-auto"
            title="Re-parse every rejected invoice from its stored PDF (use after a classifier fix)"
          >
            {reprocessAll.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Reprocess all rejected ({rejectedCount})
          </button>
        )}
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Invoices</p>
          <p className="text-2xl font-bold text-slate-800">{invoices.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Value</p>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(totalValue)}</p>
        </div>
        <div className="card p-4 sm:col-span-1 col-span-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">AWB Lines</p>
          <p className="text-2xl font-bold text-slate-800">{totalLines}</p>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : invoicesError ? (
          /* Added case: on failure `invoices` stays [] and the stat tiles above
             show 0 invoices / $0.00, which reads as "nothing ingested". */
          <QueryErrorState error={invoicesError} what="invoices" />
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <FileText size={32} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No invoices yet</p>
            <p className="text-xs text-slate-400">Drop a PDF in the Upload tab — or wait for the Gmail poller (every 15 min).</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice #</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">AWBs</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <FedexInvoiceRow
                    key={inv.id}
                    inv={inv}
                    expanded={expanded === inv.id}
                    onToggle={() => setExpanded(expanded === inv.id ? null : inv.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function FedexInvoiceRow({ inv, expanded, onToggle }) {
  return (
    <>
      <tr className="hover:bg-slate-50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3 text-slate-400">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-4 py-3 font-mono text-xs text-slate-700">{inv.invoice_number}</td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            inv.invoice_type === 'shipping'
              ? 'bg-blue-50 text-blue-700'
              : 'bg-purple-50 text-purple-700'
          }`}>
            {inv.invoice_type === 'shipping' ? 'Shipping' : 'Duty'}
          </span>
        </td>
        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{formatDate(inv.invoice_date)}</td>
        <td className="px-4 py-3 text-xs text-slate-500">{inv.source}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            {statusBadge(inv.parse_status)}
            {attemptBadge(inv.parse_attempts)}
          </div>
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{inv.line_count}</td>
        <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-700">{formatCurrency(inv.statement_total, inv.currency)}</td>
      </tr>
      {expanded && (
        <FedexInvoiceDetailRow
          invoiceId={inv.id}
          parseErrors={inv.parse_errors}
          parseStatus={inv.parse_status}
          parseAttempts={inv.parse_attempts}
        />
      )}
    </>
  )
}

function FedexInvoiceDetailRow({ invoiceId, parseErrors, parseStatus, parseAttempts }) {
  const queryClient = useQueryClient()
  const { toasts: localToasts, addToast: localAddToast, removeToast: localRemoveToast } = useToast()

  const { data: lines = [], isLoading, error: linesError } = useQuery({
    queryKey: ['fedex', 'invoice-lines', invoiceId],
    queryFn: () => getFedexInvoiceLines(invoiceId),
  })

  const reprocess = useMutation({
    mutationFn: () => reprocessFedexInvoice(invoiceId),
    onSuccess: (data) => {
      // Invalidate the invoice list so the stub disappears (or its
      // updated parse_errors shows) and any new parsed row appears.
      queryClient.invalidateQueries({ queryKey: ['fedex', 'invoices'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      const msg = data?.status === 'reprocessed'
        ? `Reprocessed: ${data.invoice_number || ''} (${data.ingest_status || 'ok'})`
        : data?.message || 'Stub cleared.'
      localAddToast({ type: 'success', message: msg, duration: 6000 })
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || 'Reprocess failed.'
      localAddToast({ type: 'error', message: detail, duration: 8000 })
    },
  })

  // Same query key as the banner in InvoicesTab, so React Query serves this
  // from cache rather than re-fetching the report per expanded row.
  const { data: incomplete } = useQuery({
    queryKey: ['fedex', 'incomplete-reconciliation'],
    queryFn: getFedexIncompleteReconciliations,
  })
  const unfinished = (incomplete?.invoices ?? []).find(r => r.invoice_id === invoiceId)

  const reconcile = useMutation({
    mutationFn: () => reconcileFedexInvoice(invoiceId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fedex', 'incomplete-reconciliation'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'overrides'] })
      const { awbs_reconciled = 0, awbs_matched = 0, cost_overrides_applied = 0 } = data || {}
      localAddToast({
        type: 'success',
        message: `Reconciled ${awbs_reconciled} AWB${awbs_reconciled === 1 ? '' : 's'} · ${awbs_matched} matched · ${cost_overrides_applied} cost override${cost_overrides_applied === 1 ? '' : 's'}`,
        duration: 8000,
      })
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || 'Reconcile failed.'
      localAddToast({ type: 'error', message: detail, duration: 8000 })
    },
  })

  return (
    <tr>
      <td colSpan={8} className="px-4 py-4 bg-slate-50/60">
        <ToastContainer toasts={localToasts} onClose={localRemoveToast} />
        {unfinished && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <strong className="font-semibold text-amber-900">
                  Reconciliation unfinished:
                </strong>{' '}
                <span className="text-amber-800">
                  {unfinished.awbs_missing} of {unfinished.awbs_total} AWBs on this invoice have
                  no {unfinished.invoice_type === 'duty' ? 'export fee' : 'reconciliation'} row.
                  What FedEx billed is stored; the billing side of it is incomplete.
                </span>
                <div className="mt-1.5 font-mono text-xs text-amber-700">
                  {unfinished.missing_awbs.slice(0, 8).join(' · ')}
                  {unfinished.awbs_missing > 8 && ` · +${unfinished.awbs_missing - 8} more`}
                </div>
              </div>
              <button
                onClick={() => reconcile.mutate()}
                disabled={reconcile.isPending}
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50"
                title="Re-run reconciliation for the AWBs listed. Leaves the header, the lines and any AWB already reconciled untouched."
              >
                {reconcile.isPending
                  ? <Loader2 size={12} className="animate-spin" />
                  : <RefreshCw size={12} />}
                Re-run reconciliation
              </button>
            </div>
          </div>
        )}
        {parseErrors && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-mono text-xs">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1"><strong className="font-semibold">Parse error:</strong> {parseErrors}</div>
              {parseStatus === 'rejected' && (
                <button
                  onClick={() => reprocess.mutate()}
                  disabled={reprocess.isPending}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                  title="Re-download the stored PDF, re-run parser + ingest. Use this after a parser fix."
                >
                  {reprocess.isPending
                    ? <Loader2 size={12} className="animate-spin" />
                    : <RefreshCw size={12} />}
                  Reprocess
                </button>
              )}
            </div>
            {Number(parseAttempts) > 1 && (
              <div className="mt-2 pt-2 border-t border-red-200 font-sans text-xs text-red-700">
                Attempt {Number(parseAttempts)} — already reprocessed{' '}
                {Number(parseAttempts) - 1} time{Number(parseAttempts) === 2 ? '' : 's'}.
                {Number(parseAttempts) >= 3 && ' Reprocessing on its own is not getting past this;' +
                  ' the classifier most likely needs a rule for the label above.'}
              </div>
            )}
          </div>
        )}
        {isLoading ? (
          <p className="text-xs text-slate-400">Loading lines…</p>
        ) : linesError ? (
          /* `lines` defaults to [], so a failed fetch would claim the invoice
             parsed with no AWB lines at all. */
          <p className="text-xs text-red-600 flex items-start gap-1.5">
            <AlertTriangle size={12} className="text-red-500 shrink-0 mt-0.5" />
            <span>
              Couldn't load AWB lines — this is a load failure, not an invoice without lines.{' '}
              <span className="font-mono text-slate-400 break-all">
                {linesError?.response?.data?.detail || linesError?.message || 'Unknown error'}
              </span>
            </span>
          </p>
        ) : lines.length === 0 ? (
          <p className="text-xs text-slate-400">No AWB lines recorded for this invoice.</p>
        ) : (
          <div className="space-y-2">
            {lines.map((ln) => (
              <div key={ln.id} className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm">
                    <span className="font-mono font-medium text-slate-700">{ln.awb_number}</span>
                    {ln.recipient_name && <span className="text-slate-400 ml-2">→ {ln.recipient_name}</span>}
                  </p>
                  <p className="text-sm font-semibold text-slate-700 tabular-nums">{formatCurrency(ln.line_total)}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-500">
                  {ln.ship_date && <div><span className="text-slate-400">Ship: </span>{formatDate(ln.ship_date)}</div>}
                  {ln.service_type && <div><span className="text-slate-400">Service: </span>{ln.service_type}</div>}
                  {ln.weight_kg != null && <div><span className="text-slate-400">Weight: </span>{ln.weight_kg} kg</div>}
                  {ln.dimension && <div><span className="text-slate-400">Dim: </span>{ln.dimension}</div>}
                </div>
                {ln.charges?.length > 0 && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {ln.charges.map((c, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-slate-500">{c.label}</span>
                        <span className={`tabular-nums font-mono ${c.amount < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                          {formatCurrency(c.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  )
}

// ─── Worker Status Card ───────────────────────────────────────────────────────
//
// Now a thin wrapper around the shared components/admin/WorkerStatusCard.
// The shared card handles the status pill, heartbeat row, and inline
// error/stale banners — we just supply the per-worker query + stat labels.

function FedexWorkerStatusCard() {
  return (
    <WorkerStatusCard
      workerName="fedex-ingest-poll"
      queryKey={['fedex', 'worker-status']}
      queryFn={getFedexWorkerStatus}
      stalePathHint="DigitalOcean → Workers → fedex-ingest-poll"
      statsConfig={[
        { key: 'parsed',    label: 'parsed',   accent: 'emerald' },
        { key: 'duplicate', label: 'dup',      accent: 'slate'   },
        { key: 'rejected',  label: 'rejected', accent: 'amber'   },
      ]}
    />
  )
}

// Keep the helper around for the rare case anything else in this file
// formats a timestamp — but the card uses its own copy now.
function fmtRelative(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  if (Number.isNaN(diffMs)) return '—'
  const s = Math.round(diffMs / 1000)
  if (s < 5)       return 'just now'
  if (s < 60)      return `${s}s ago`
  if (s < 3600)    return `${Math.round(s / 60)}m ago`
  if (s < 86400)   return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

// (Inline WorkerStatusCard was extracted to components/admin/WorkerStatusCard.jsx.
//  See FedexWorkerStatusCard above for the FedEx-specific wiring.)

// ─── Reconciliation Tab ───────────────────────────────────────────────────────

function ReconciliationTab() {
  const [showOnly, setShowOnly] = useState('all')  // all | flagged | unmatched | pending
  const queryClient = useQueryClient()
  const { toasts, addToast } = useToast()

  const params = useMemo(() => {
    if (showOnly === 'flagged')    return { flagged: true }
    if (showOnly === 'unmatched')  return { matched: false }
    if (showOnly === 'pending')    return { resolution: 'pending' }
    return {}
  }, [showOnly])

  const { data: rows = [], isLoading, error: rowsError } = useQuery({
    queryKey: ['fedex', 'reconciliations', params],
    queryFn: () => getFedexReconciliations(params),
    refetchInterval: 60_000,
  })

  const resolveMut = useMutation({
    mutationFn: ({ reconId, resolution }) =>
      updateFedexReconciliationResolution(reconId, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      addToast({ type: 'success', message: 'Resolution updated' })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Failed to update',
    }),
  })

  const counts = useMemo(() => ({
    total:     rows.length,
    matched:   rows.filter(r => r.order_id).length,
    flagged:   rows.filter(r => r.flagged).length,
    pending:   rows.filter(r => r.resolution === 'pending').length,
    variance:  rows.reduce((s, r) => s + (Math.abs(r.variance_abs) || 0), 0),
  }), [rows])

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} />

      {/* Gmail-poller heartbeat — answers "is the ingest worker doing OK?" */}
      <FedexWorkerStatusCard />

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'all',       label: 'All' },
          { key: 'flagged',   label: 'Flagged' },
          { key: 'unmatched', label: 'Unmatched' },
          { key: 'pending',   label: 'Pending review' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setShowOnly(key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              showOnly === key
                ? 'bg-blue-100 text-blue-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total AWBs</p>
          <p className="text-2xl font-bold text-slate-800">{counts.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Matched</p>
          <p className="text-2xl font-bold text-emerald-600">{counts.matched}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Flagged</p>
          <p className={`text-2xl font-bold ${counts.flagged > 0 ? 'text-amber-600' : 'text-slate-800'}`}>{counts.flagged}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Variance (Σ)</p>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(counts.variance)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : rowsError ? (
          /* Added case: `rows` defaults to [], so a failed fetch would show 0
             flagged AWBs and $0.00 total variance — a clean reconciliation. */
          <QueryErrorState error={rowsError} what="reconciliations" />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <GitMerge size={32} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No reconciliations match</p>
            <p className="text-xs text-slate-400">Try a different filter, or ingest a shipping invoice in the Upload tab.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">AWB</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">FedEx</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">ShipStation</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Variance</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Match</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className={`hover:bg-slate-50 transition-colors ${r.flagged ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.awb_number}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.order_number || <span className="text-slate-400 italic">Unmatched</span>}</td>
                    <td className="px-4 py-3 text-slate-600">{r.client_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatCurrency(r.fedex_shipping_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{formatCurrency(r.shipstation_cost)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
                      r.variance_abs > 0 ? 'text-red-600' : r.variance_abs < 0 ? 'text-emerald-600' : 'text-slate-500'
                    }`}>
                      {r.flagged && (
                        <span title={r.flag_reason || 'Flagged for review'}>
                          <Flag size={11} className="inline mr-1 text-amber-500" />
                        </span>
                      )}
                      {formatCurrency(r.variance_abs)}
                      {r.variance_pct != null && (
                        <span className="text-xs text-slate-400 ml-1">
                          ({(r.variance_pct * 100).toFixed(1)}%)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.match_method && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {r.match_method}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{resolutionBadge(r.resolution, r.reviewed_by)}</td>
                    <td className="px-4 py-3">
                      {r.resolution === 'pending' && (
                        <div className="flex items-center gap-1">
                          {r.order_id && (
                            <>
                              <button
                                onClick={() => resolveMut.mutate({ reconId: r.id, resolution: 'accepted' })}
                                disabled={resolveMut.isPending}
                                className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => resolveMut.mutate({ reconId: r.id, resolution: 'disputed' })}
                                disabled={resolveMut.isPending}
                                className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100"
                              >
                                Dispute
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => resolveMut.mutate({ reconId: r.id, resolution: 'dismissed' })}
                            disabled={resolveMut.isPending}
                            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                            title="Clear this AWB from the queue without billing it — e.g. a FedEx credit/adjustment that can't reconcile."
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Exceptions Tab ───────────────────────────────────────────────────────────
//
// Unmatched AWBs (order_id is null on the recon row). The matcher couldn't
// find an order, so an admin manually links one here. Drives the assign
// endpoint: PATCH /admin/fedex/reconciliations/{id}/assign
function ExceptionsTab() {
  const queryClient = useQueryClient()
  const { toasts, addToast } = useToast()
  // expandedReconId controls which row's search box is open. Only one at a time.
  const [expandedReconId, setExpandedReconId] = useState(null)

  const { data: rawRows = [], isLoading, error: rowsError } = useQuery({
    queryKey: ['fedex', 'reconciliations', { matched: false }],
    queryFn: () => getFedexReconciliations({ matched: false }),
    refetchInterval: 60_000,
  })
  // A dismissed AWB (e.g. a FedEx credit) needs no order link — keep it out
  // of the "link to an order" exceptions pile.
  const rows = useMemo(() => rawRows.filter(r => r.resolution !== 'dismissed'), [rawRows])

  const assignMut = useMutation({
    mutationFn: ({ reconId, orderId }) => assignFedexReconciliation(reconId, orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      addToast({ type: 'success', message: 'AWB linked to order' })
      setExpandedReconId(null)
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Failed to assign',
    }),
  })

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Unmatched AWBs</p>
          <p className={`text-2xl font-bold ${rows.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{rows.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">FedEx Charges (unallocated)</p>
          <p className="text-2xl font-bold text-slate-800">
            {formatCurrency(rows.reduce((s, r) => s + (r.fedex_shipping_total || 0), 0))}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Heuristic candidates</p>
          <p className="text-2xl font-bold text-slate-800">
            {rows.filter(r => r.match_method === 'heuristic').length}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">low-confidence matches still in the pile</p>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : rowsError ? (
          /* Added case: `rawRows` defaults to [], so a failed fetch would show a
             green tick claiming every AWB matched to an order and $0.00
             unallocated FedEx charges. */
          <QueryErrorState error={rowsError} what="unmatched AWBs" />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <CheckCircle2 size={32} className="text-emerald-400" />
            <p className="text-sm font-medium text-slate-600">No exceptions</p>
            <p className="text-xs text-slate-400">Every AWB on every invoice matched to an order.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">AWB</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">FedEx total</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last seen</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <UnmatchedRow
                    key={r.id}
                    row={r}
                    expanded={expandedReconId === r.id}
                    onToggle={() => setExpandedReconId(expandedReconId === r.id ? null : r.id)}
                    onAssign={(orderId) => assignMut.mutate({ reconId: r.id, orderId })}
                    isAssigning={assignMut.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function UnmatchedRow({ row, expanded, onToggle, onAssign, isAssigning }) {
  const [query, setQuery] = useState('')
  // Order search only runs when query has ≥ 2 chars. Hits /admin/orders?search=.
  const { data: searchData, isFetching } = useQuery({
    queryKey: ['orders', 'search', query],
    queryFn: () => getAdminOrders({ search: query, limit: 8 }),
    enabled: expanded && query.length >= 2,
    staleTime: 30_000,
  })
  const candidates = searchData?.orders || []

  return (
    <>
      <tr className="hover:bg-slate-50 transition-colors">
        <td className="px-4 py-3 font-mono text-xs text-slate-700">{row.awb_number}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatCurrency(row.fedex_shipping_total)}</td>
        <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(row.last_updated_at)}</td>
        <td className="px-4 py-3">
          <button
            onClick={onToggle}
            className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1"
          >
            <Link2 size={12} />
            {expanded ? 'Cancel' : 'Link to order'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-blue-50/30">
          <td colSpan={4} className="px-4 py-3">
            <div className="space-y-2 max-w-2xl">
              <label className="text-xs font-medium text-slate-600">
                Search for an order by order # or recipient name:
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. SO-12345 or recipient name"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              {query.length < 2 ? (
                <p className="text-xs text-slate-400">Type at least 2 characters to search.</p>
              ) : isFetching ? (
                <p className="text-xs text-slate-400 flex items-center gap-1.5">
                  <Loader2 size={11} className="animate-spin" /> Searching…
                </p>
              ) : candidates.length === 0 ? (
                <p className="text-xs text-slate-400">No orders found for "{query}".</p>
              ) : (
                <ul className="bg-white border border-slate-200 rounded divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  {candidates.map((o) => (
                    <li key={o.id}>
                      <button
                        onClick={() => onAssign(o.id)}
                        disabled={isAssigning}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-mono text-slate-700 truncate">{o.order_number || '—'}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {o.recipient_name || '—'}
                              {o.tracking_number && (
                                <span className="ml-2 font-mono text-slate-400">{o.tracking_number}</span>
                              )}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-slate-500">{o.carrier || '—'}</p>
                            <p className="text-xs text-slate-400">{formatDate(o.shipped_date)}</p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Disputes Tab ─────────────────────────────────────────────────────────────
//
// The dispute workflow surface. Two row types are surfaced together so the
// dispute-handler can see (a) what's actively in dispute, and (b) what's
// flagged-and-still-pending that they should triage. Resolved disputes turn
// into either 'accepted' (FedEx granted credit) or 'written_off' (we ate it).
function DisputesTab() {
  const queryClient = useQueryClient()
  const { toasts, addToast } = useToast()

  const { data: disputed = [], error: disputedError } = useQuery({
    queryKey: ['fedex', 'reconciliations', { resolution: 'disputed' }],
    queryFn: () => getFedexReconciliations({ resolution: 'disputed' }),
    refetchInterval: 60_000,
  })
  const { data: flaggedPending = [], error: flaggedError } = useQuery({
    queryKey: ['fedex', 'reconciliations', { flagged: true, resolution: 'pending' }],
    queryFn: () => getFedexReconciliations({ flagged: true, resolution: 'pending' }),
    refetchInterval: 60_000,
  })

  const resolveMut = useMutation({
    mutationFn: ({ reconId, resolution }) =>
      updateFedexReconciliationResolution(reconId, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fedex', 'reconciliations'] })
      addToast({ type: 'success', message: 'Resolution updated' })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Failed to update',
    }),
  })

  const totalDisputedVariance = useMemo(
    () => disputed.reduce((s, r) => s + (r.variance_abs || 0), 0),
    [disputed],
  )

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Active disputes</p>
          <p className={`text-2xl font-bold ${disputed.length > 0 ? 'text-amber-600' : 'text-slate-800'}`}>
            {disputed.length}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Disputed variance</p>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(totalDisputedVariance)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Flagged & pending triage</p>
          <p className={`text-2xl font-bold ${flaggedPending.length > 0 ? 'text-amber-600' : 'text-slate-800'}`}>
            {flaggedPending.length}
          </p>
        </div>
      </div>

      {/* Active disputes */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <MessageSquareWarning size={14} className="text-amber-500" />
            Active disputes
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden sm:inline">FedEx hasn't credited (or denied) these yet.</span>
            <button
              onClick={async () => {
                try {
                  await exportFedexDisputes('disputed')
                  addToast({ type: 'success', message: 'CSV downloaded' })
                } catch (err) {
                  addToast({
                    type: 'error',
                    message: err.response?.data?.detail || 'Export failed',
                  })
                }
              }}
              disabled={disputed.length === 0}
              className="text-xs px-2.5 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
              title="Download the active disputes as a CSV evidence pack for FedEx"
            >
              <Download size={12} />
              Export CSV
            </button>
          </div>
        </div>
        {disputedError ? (
          /* `disputed` defaults to [], so a failed fetch would report no active
             disputes and $0.00 disputed variance — money we'd stop chasing. */
          <QueryErrorState error={disputedError} what="active disputes" dense />
        ) : disputed.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            No active disputes.
          </div>
        ) : (
          <DisputesTable rows={disputed} mode="disputed" onResolve={resolveMut.mutate} isPending={resolveMut.isPending} />
        )}
      </div>

      {/* Flagged & pending triage */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Flag size={14} className="text-amber-500" />
            Flagged & pending triage
          </h3>
          <span className="text-xs text-slate-400">Material variance ({'>'} $2 and {'>'} 5%) — accept or open as dispute.</span>
        </div>
        {flaggedError ? (
          /* `flaggedPending` defaults to [], so a failed fetch would say
             "Nothing flagged" and leave material variances untriaged. */
          <QueryErrorState error={flaggedError} what="flagged AWBs" dense />
        ) : flaggedPending.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            Nothing flagged.
          </div>
        ) : (
          <DisputesTable rows={flaggedPending} mode="flagged" onResolve={resolveMut.mutate} isPending={resolveMut.isPending} />
        )}
      </div>
    </div>
  )
}

function DisputesTable({ rows, mode, onResolve, isPending }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">AWB</th>
            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">FedEx</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">ShipStation</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Variance</th>
            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.awb_number}</td>
              <td className="px-4 py-3 font-mono text-xs text-slate-600">{r.order_number || '—'}</td>
              <td className="px-4 py-3 text-slate-600">{r.client_name || '—'}</td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatCurrency(r.fedex_shipping_total)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-600">{formatCurrency(r.shipstation_cost)}</td>
              <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
                r.variance_abs > 0 ? 'text-red-600' : r.variance_abs < 0 ? 'text-emerald-600' : 'text-slate-500'
              }`}>
                {formatCurrency(r.variance_abs)}
                {r.variance_pct != null && (
                  <span className="text-xs text-slate-400 ml-1">
                    ({(r.variance_pct * 100).toFixed(1)}%)
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  {mode === 'disputed' ? (
                    <>
                      <button
                        onClick={() => onResolve({ reconId: r.id, resolution: 'accepted' })}
                        disabled={isPending}
                        className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        title="FedEx granted credit — close the dispute"
                      >
                        Credited
                      </button>
                      <button
                        onClick={() => onResolve({ reconId: r.id, resolution: 'written_off' })}
                        disabled={isPending}
                        className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                        title="FedEx denied — write off the variance"
                      >
                        Write off
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => onResolve({ reconId: r.id, resolution: 'accepted' })}
                        disabled={isPending}
                        className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => onResolve({ reconId: r.id, resolution: 'disputed' })}
                        disabled={isPending}
                        className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100"
                      >
                        Open dispute
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Cost Overrides Tab ───────────────────────────────────────────────────────
//
// Read-only audit list of every change to an order's effective_courier_cost.
// Two sources: 'fedex_reconciliation' (auto from ingest) and 'manual'. Active
// overrides are the ones currently in force on the order; inactive ones are
// history (superseded by a newer override).
function CostOverridesTab() {
  const [filter, setFilter] = useState('active')   // active | all | manual | fedex

  const params = useMemo(() => {
    if (filter === 'active') return { active_only: true }
    if (filter === 'manual') return { source: 'manual' }
    if (filter === 'fedex')  return { source: 'fedex_reconciliation' }
    return {}
  }, [filter])

  const { data: rows = [], isLoading, error: rowsError } = useQuery({
    queryKey: ['fedex', 'overrides', params],
    queryFn: () => getFedexCourierCostOverrides(params),
    refetchInterval: 60_000,
  })

  const counts = useMemo(() => ({
    total:  rows.length,
    active: rows.filter(r => r.active).length,
    manual: rows.filter(r => r.source === 'manual').length,
    delta:  rows.reduce((s, r) => s + ((r.override_cost || 0) - (r.original_cost || 0)), 0),
  }), [rows])

  return (
    <div className="space-y-4">
      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'active', label: 'Active' },
          { key: 'all',    label: 'All (incl. history)' },
          { key: 'fedex',  label: 'Auto (FedEx)' },
          { key: 'manual', label: 'Manual' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Rows shown</p>
          <p className="text-2xl font-bold text-slate-800">{counts.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Active</p>
          <p className="text-2xl font-bold text-slate-800">{counts.active}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Manual</p>
          <p className="text-2xl font-bold text-slate-800">{counts.manual}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Net delta</p>
          <p className={`text-2xl font-bold ${counts.delta > 0 ? 'text-red-600' : counts.delta < 0 ? 'text-emerald-600' : 'text-slate-800'}`}>
            {formatCurrency(counts.delta)}
          </p>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : rowsError ? (
          /* Added case: `rows` defaults to [], so a failed fetch would claim no
             cost override has ever changed a per-order cost, net delta $0.00. */
          <QueryErrorState error={rowsError} what="cost overrides" />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <History size={32} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No overrides yet</p>
            <p className="text-xs text-slate-400">Cost overrides land here once FedEx ingest or a manual edit changes a per-order cost.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">When</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Original</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Override</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Δ</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const delta = (r.override_cost || 0) - (r.original_cost || 0)
                  return (
                    <tr key={r.id} className={`hover:bg-slate-50 transition-colors ${!r.active ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 text-xs text-slate-500">{formatDate(r.set_at)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.order_number || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{r.client_name || '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {r.original_cost != null ? formatCurrency(r.original_cost) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700 font-medium">
                        {formatCurrency(r.override_cost)}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {formatCurrency(delta)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          r.source === 'manual'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {r.source === 'manual' ? 'Manual' : 'FedEx'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate" title={r.reason || ''}>
                        {r.reason || <span className="text-slate-400 italic">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.active ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Active</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Superseded</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function FedexReconciliation() {
  const [activeTab, setActiveTab] = useState('reconciliation')

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">FedEx Reconciliation</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Match FedEx invoice line items against ShipStation costs for client billing accuracy and dispute evidence.
        </p>
      </div>

      {/* Tab strip — same pattern as Unmatched Exports */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        <TabButton id="upload"         activeTab={activeTab} setActiveTab={setActiveTab} icon={UploadIcon}>Upload</TabButton>
        <TabButton id="invoices"       activeTab={activeTab} setActiveTab={setActiveTab} icon={FileText}>Invoices</TabButton>
        <TabButton id="reconciliation" activeTab={activeTab} setActiveTab={setActiveTab} icon={GitMerge}>Reconciliation</TabButton>
        <TabButton id="exceptions"     activeTab={activeTab} setActiveTab={setActiveTab} icon={AlertCircle}>Exceptions</TabButton>
        <TabButton id="disputes"       activeTab={activeTab} setActiveTab={setActiveTab} icon={MessageSquareWarning}>Disputes</TabButton>
        <TabButton id="overrides"      activeTab={activeTab} setActiveTab={setActiveTab} icon={History}>Cost Overrides</TabButton>
      </div>

      {activeTab === 'upload'         && <UploadTab />}
      {activeTab === 'invoices'       && <InvoicesTab />}
      {activeTab === 'reconciliation' && <ReconciliationTab />}
      {activeTab === 'exceptions'     && <ExceptionsTab />}
      {activeTab === 'disputes'       && <DisputesTab />}
      {activeTab === 'overrides'      && <CostOverridesTab />}
    </div>
  )
}

function TabButton({ id, activeTab, setActiveTab, icon: Icon, children }) {
  const isActive = activeTab === id
  return (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        isActive
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon size={14} />
      {children}
    </button>
  )
}
